import { useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { CONCEPT_NAME, TApi, TRoles } from '../../../../shared/const'
import http from '../../../../shared/http'
import eventEmitter, { types } from '../../../../shared/eventEmitter'
import { blobTypes, downloadFile, stateLabel } from '../../../../shared/helpers'
import { useIsReadOnly } from '../../../../shared/alumni'
import useDebouncedValue from '../../../../shared/useDebouncedValue'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'

import Btn from '../../../../UI/Btn'
import BtnFilter from '../../../../UI/BtnFilter'
import FilterPanel from '../../../../UI/FilterPanel'
import useFilters from '../../../../UI/FilterPanel/useFilters'
import { companyTypeField, conceptStageField, genderField, stateField } from '../../../../UI/FilterPanel/fields'
import Icon from '../../../../UI/Icon'
import Loading from '../../../../UI/Loading'
import MiniHeader from '../../../../UI/MiniHeader'
import HandbookButton from '../../common/Concept/HandbookButton'
import Pagination from '../../../../UI/Pagination'
import Search from '../../../../UI/Search'
import StatusFunnel from '../../../../UI/StatusFunnel'
import Table from '../../../../UI/Table'

import ConceptDialog from './ConceptDialog'

import styles from './style.module.scss'

/**
 * Auswertung zum Skills4Retail-Konzept.
 *
 * Zwei Reiter, zwei Fragen: „Wie weit ist das Programm insgesamt?" und „Wer
 * genau steht wo?".
 *
 * Der Trichter im Überblick ist derselbe Baustein wie der Statusfilter der
 * Anforderungen (`UI/StatusFunnel`), hier aber mit **einfacher** Auswahl: Die
 * Stufen bauen aufeinander auf, „bewertet" ist immer eine Teilmenge von
 * „abgeschlossen". Ein Klick auf eine Stufe wechselt in den zweiten Reiter und
 * setzt sie dort als Filter — damit ist jede Zahl bis zur einzelnen Person
 * nachvollziehbar und bleibt keine Behauptung.
 *
 * Gerechnet wird ausschließlich im Backend (`services/concept/report`). Würde
 * die Oberfläche nachzählen, gäbe es zwei Ergebnisse für dieselbe Frage.
 *
 * Ein Klick auf eine Zeile öffnet das Konzept dahinter (`ConceptDialog`) — die
 * fünfzehn Felder mit Text und Bewertung, zum Lesen. Damit endet die Auswertung
 * nicht bei der Zahl, sondern bei dem, was die Junior Company geschrieben hat.
 */

const TABS = [
	{ key: 'overview', label: 'Überblick' },
	{ key: 'students', label: 'Schüler:innen' }
]

const CONCEPT_GER = {
	DRAFT: 'In Arbeit',
	SUBMITTED: 'Zur Bewertung',
	RETURNED: 'Zur Überarbeitung',
	SCORED: 'Bewertet'
}

const GENDER_GER = { MALE: 'Männlich', FEMALE: 'Weiblich', DIVERS: 'Vielfältig' }

const LIMIT = 25

function ConceptReport(props) {
	const { t } = useTranslation()

	const [tab, setTab] = useState('overview')
	const scopeRef = useRef(null)
	useScrollTopOnTabChange(tab, scopeRef)

	const [overview, setOverview] = useState(null)
	const [overviewLoading, setOverviewLoading] = useState(true)

	const [rows, setRows] = useState([])
	const [countAll, setCountAll] = useState(0)
	const [page, setPage] = useState(0)
	const [sort, setSort] = useState({ name: 'name', direction: 'asc' })
	const [search, setSearch] = useState('')
	const [showFilter, setShowFilter] = useState(false)
	const [loading, setLoading] = useState(false)
	const [downloading, setDownloading] = useState(false)
	const readOnly = useIsReadOnly()

	// Ein Landesadmin sieht ohnehin nur das eigene Bundesland (das setzt das
	// Backend) — ein Filter darauf waere eine Auswahl ohne Wirkung.
	const fields = useMemo(
		() => [
			conceptStageField(overview?.stages),
			...(props.role === TRoles.ADMIN ? [] : [stateField(t)]),
			companyTypeField(t),
			genderField(t)
		],
		[t, props.role, overview]
	)

	const filters = useFilters({ storageKey: 'conceptReport', fields })
	const filterQuery = JSON.stringify(filters.query)
	const debouncedSearch = useDebouncedValue(search)

	// Eine Antwort, die nach dem Verlassen der Seite eintrifft, darf nicht mehr
	// in den Zustand schreiben.
	const mounted = useRef(true)
	useEffect(() => () => (mounted.current = false), [])

	/* ------------------------------------------------------------ Überblick */

	useEffect(() => {
		setOverviewLoading(true)
		http.get(TApi.ADMIN_CONCEPT_OVERVIEW)
			.then(({ data }) => mounted.current && setOverview(data))
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => mounted.current && setOverviewLoading(false))
	}, [])

	/* ---------------------------------------------------------------- Liste */

	useEffect(() => {
		// Erst laden, wenn der Reiter sichtbar ist: Die Auswertung liest den
		// gesamten (eingegrenzten) Bestand — das gehoert nicht beim Seitenaufruf
		// nebenher angestossen.
		if (tab !== 'students') return

		setLoading(true)
		http.get(TApi.ADMIN_CONCEPT_STUDENTS, {
			params: {
				limit: LIMIT,
				offset: page * LIMIT,
				order: sort.name,
				direction: sort.direction,
				search: debouncedSearch,
				filter: filterQuery
			}
		})
			.then(({ data }) => {
				if (!mounted.current) return
				setRows(data.rows || [])
				setCountAll(data.count || 0)
			})
			.catch(() => {})
			.finally(() => mounted.current && setLoading(false))
	}, [tab, page, sort, debouncedSearch, filterQuery])

	/* -------------------------------------------------------------- Aktionen */

	const handleFilterChange = (key, selected) => {
		setPage(0)
		filters.setField(key, selected)
	}

	const handleFilterReset = () => {
		setPage(0)
		filters.resetAll()
	}

	const handleSearchChange = value => {
		setPage(0)
		setSearch(value)
	}

	/*
	 * Ein Klick auf eine Stufe des Trichters ist eine Kurzwahl auf dasselbe
	 * Filterfeld — kein zweiter Zustand daneben. Die Stufen bauen aufeinander
	 * auf, deshalb ersetzt die Auswahl die vorige, statt sich dazuzustellen.
	 */
	const waehleStufe = next => {
		const stage = next.length ? next[next.length - 1] : null
		const option = (overview?.stages || []).find(entry => entry.key === stage)
		setPage(0)
		filters.setField('stage', option ? [{ value: option.key, label: option.label }] : [])
		if (option) setTab('students')
	}

	const herunterladen = () => {
		setDownloading(true)
		http.get(TApi.ADMIN_CONCEPT_DOWNLOAD, {
			responseType: 'arraybuffer',
			params: {
				order: sort.name,
				direction: sort.direction,
				search: debouncedSearch,
				filter: filterQuery
			}
		})
			.then(({ data }) => {
				// Mit dem Tag im Namen bleiben zwei Auswertungen im
				// Download-Ordner nebeneinander liegen, statt sich zu ueberschreiben.
				const tag = new Date().toISOString().slice(0, 10)
				downloadFile(`Skills4Retail-Konzept-${tag}.xlsx`, new Blob([data], { type: blobTypes.XLS }))
			})
			.catch(() => {})
			.finally(() => mounted.current && setDownloading(false))
	}

	/* -------------------------------------------------------------- Trichter */

	// Eigenes useMemo, damit die drei Ableitungen darunter nicht bei jedem
	// Rendern eine neue Liste sehen.
	const stufen = useMemo(() => overview?.stages || [], [overview])
	const counts = useMemo(() => Object.fromEntries(stufen.map(s => [s.key, s.students])), [stufen])
	const secondary = useMemo(() => Object.fromEntries(stufen.map(s => [s.key, `${s.companies} Companies`])), [stufen])
	const hints = useMemo(() => Object.fromEntries(stufen.map(s => [s.key, s.hint])), [stufen])
	const gewaehlteStufe = filters.values.stage?.[0]?.value

	/* -------------------------------------------------------------- Spalten */

	const columns = useMemo(
		() => [
			{ Header: 'Name', accessor: 'name', width: 180, minWidth: 140 },
			{ Header: 'Alter', accessor: 'age', align: 'center', width: 60, Cell: ({ value }) => value ?? '—' },
			{
				Header: 'Geschlecht',
				accessor: 'gender',
				width: 100,
				Cell: ({ value }) => (value ? GENDER_GER[value] || value : '—')
			},
			{
				Header: 'Bundesland',
				accessor: 'state',
				width: 90,
				Cell: ({ value }) => (value ? stateLabel(value) : '—')
			},
			{ Header: 'Schule', accessor: 'schoolName', width: 190, Cell: ({ value }) => value || '—' },
			{ Header: 'Ort', accessor: 'schoolLocation', width: 110, Cell: ({ value }) => value || '—' },
			{ Header: 'Junior Company', accessor: 'companyName', width: 170, Cell: ({ value }) => value || '—' },
			{ Header: 'Lehrkraft', accessor: 'teacherName', width: 150, Cell: ({ value }) => value || '—' },
			{
				Header: 'Kapitel',
				accessor: 'chaptersPassed',
				align: 'center',
				width: 80,
				headerToolTip: 'Bestandene Kapitel der Lernstrecke',
				Cell: ({ row }) => `${row.original.chaptersPassed} / ${row.original.chaptersTotal}`
			},
			{
				Header: 'Konzept',
				accessor: 'conceptStatus',
				width: 120,
				Cell: ({ value }) => (value ? CONCEPT_GER[value] || value : 'Nicht begonnen')
			},
			{
				Header: 'Score',
				accessor: 'scorePercent',
				align: 'center',
				width: 80,
				headerToolTip: 'Punkte der zugeteilten Konzeptfelder im Verhältnis zum Erreichbaren',
				Cell: ({ value }) => (value === null || value === undefined ? '—' : `${value} %`)
			}
		],
		[]
	)

	return (
		<div className={styles.root} ref={scopeRef}>
			<MiniHeader className={styles.miniHeader} title="Skills4Retail-Konzept">
				<div className={styles.miniHeaderContent}>
					{/* Das Handbuch, mit dem SuS und LuL arbeiten (Anforderung #187) —
					    vor Suche und Filter, die rechts außen bleiben. */}
					<HandbookButton />
					{tab === 'students' && (
						<>
							<Search
								value={search}
								placeholder="Name, Company, Schule, Ort oder Lehrkraft"
								onChange={e => handleSearchChange(e.target.value)}
								onSubmit={e => e.preventDefault()}
							/>
							<BtnFilter icon="arrow" onClick={() => setShowFilter(true)} count={filters.count}>
								{t('Filter')} ({filters.count})
							</BtnFilter>
						</>
					)}
				</div>
			</MiniHeader>

			<div className={styles.tabs} role="tablist">
				{TABS.map(entry => (
					<button
						key={entry.key}
						type="button"
						role="tab"
						aria-selected={tab === entry.key}
						className={clsx(styles.tab, tab === entry.key && styles.tabActive)}
						onClick={() => setTab(entry.key)}
					>
						{entry.label}
					</button>
				))}
			</div>

			{tab === 'overview' && (
				<>
					{overviewLoading && <Loading />}

					{overview && (
						<>
							<div className={styles.cards}>
								<div className={styles.card}>
									<span className={styles.cardLabel}>Schüler:innen</span>
									<span className={styles.cardValue}>{overview.population.students}</span>
								</div>
								<div className={styles.card}>
									<span className={styles.cardLabel}>Lehrkräfte</span>
									<span className={styles.cardValue}>{overview.population.teachers}</span>
								</div>
								<div className={styles.card}>
									<span className={styles.cardLabel}>Junior Companies</span>
									<span className={styles.cardValue}>{overview.population.companies}</span>
								</div>
							</div>

							<StatusFunnel
								counts={counts}
								secondary={secondary}
								hints={hints}
								options={stufen.map(stage => ({ value: stage.key, label: stage.label }))}
								value={gewaehlteStufe ? [gewaehlteStufe] : []}
								onChange={waehleStufe}
								label="Fortschritt beim Konzept"
								single
							/>

							<p className={styles.note}>
								Die große Zahl zählt <b>Schüler:innen</b>, die kleine darunter <b>Junior Companies</b>.
								Ein Klick auf eine Stufe öffnet die Liste dahinter. Ein Kapitel gilt ab{' '}
								<b>{overview.quizPass} von 5 richtigen Antworten</b> als bestanden — der Wert steht in
								den Einstellungen unter „Checkliste & Voraussetzungen".
							</p>
						</>
					)}
				</>
			)}

			{tab === 'students' && (
				<>
					<div className={styles.subHeader}>
						<Pagination
							limit={LIMIT}
							page={page}
							label="Schüler:innen"
							countAll={countAll}
							onChange={setPage}
						/>
						{/* Alumni (#213): keine Excel-Listen. */}
						{!readOnly && (
							<div className={styles.downloadContent}>
								Liste herunterladen als
								<div>
									{!downloading && (
										<Btn
											theme="download"
											iconSize={{ w: 24, h: 24 }}
											icon="download"
											onClick={herunterladen}
										>
											XLS
										</Btn>
									)}
									{downloading && (
										<Icon
											className={styles.rootLoading}
											size={{ w: 16, h: 16 }}
											name="loading"
											color="#22404D"
										/>
									)}
								</div>
							</div>
						)}
					</div>

					{/*
					 * Der Name des Konzepts steht hier und nicht in der Überschrift:
					 * Dort sitzen Suche und Filter, und „Skills4Retail-Konzept:
					 * Marketing & E-Commerce" drängte beide an den Rand.
					 */}
					<p className={styles.rowHint}>
						Ein Klick auf eine Zeile öffnet das Konzept „{CONCEPT_NAME}“ der Junior Company dahinter — die
						fünfzehn Felder mit Text und Bewertung, zum Lesen.
					</p>

					{loading && <Loading />}

					<div className="container">
						<Table
							columns={columns}
							items={rows}
							page={page}
							limit={LIMIT}
							countAll={countAll}
							onChange={setPage}
							onRowClick={row => eventEmitter.emit(types.openConceptDetail, row)}
							// Ohne Junior Company gibt es kein Konzept — eine Zeile, die
							// sich oeffnen laesst und dann nichts zeigt, waere ein
							// gebrochenes Versprechen.
							isRowClickable={row => Boolean(row.companyId)}
							handleSort={(name, direction) => setSort({ name, direction })}
						/>
					</div>
				</>
			)}

			<ConceptDialog />

			<FilterPanel
				open={showFilter}
				onClose={() => setShowFilter(false)}
				fields={fields}
				values={filters.values}
				count={filters.count}
				onChange={handleFilterChange}
				onResetAll={handleFilterReset}
			/>
		</div>
	)
}

const mapStateToProps = state => ({
	role: state.user.role
})

export default connect(mapStateToProps)(ConceptReport)
